// useGCCV2TabResult.ts
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChat } from "../../../contexts/ChatContext";
import { useCategories } from "../../../contexts/CategoryContext";
import { useSubCategories } from "../../../contexts/SubCategoryContext";
import { getStoredAstriaGCCV2Dob } from "../../../utils/GCC/astriaGCCV2Dob";
import {
  useGCCV2Language,
  GCCV2Language,
  getGCCV2StrictLanguageInstruction,
} from "../../../utils/GCC/astriaGCCV2Language";
import {
  parseGCCV2Response,
  GCCV2ParsedResponse,
} from "../../../utils/GCC/astriaGCCV2Markdown";

// ─── Shared fetch/parse logic for the 6 conversational GCC v2 tabs. Each
// tab result component supplies its own subcategory-name matcher and
// prompt copy; this hook handles: DOB gating, resolving categoryId/
// subCategoryId by name (never hardcoded IDs, since DB IDs vary by
// environment), finding/creating the chat, calling sendMessageStream,
// reading back the streamed AI reply, caching it in sessionStorage, and
// parsing it into the 5-part response structure.

const GCC_V2_CATEGORY_NAME = "Astria GCC V2";

const GENERIC_ERROR: Record<GCCV2Language, string> = {
  en: "Something went wrong. Please try again.",
  ar: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
};

const NEW_CHAT_ERROR: Record<GCCV2Language, string> = {
  en: "Failed to start new chat. Please try again.",
  ar: "تعذر بدء محادثة جديدة. يرجى المحاولة مرة أخرى.",
};

interface UseGCCV2TabResultOptions {
  tabKey: string; // used for sessionStorage cache key, e.g. "timing"
  subCategoryMatch: string; // lowercase substring to find the subcategory, e.g. "timing"
  buildPrompt: (dob: string, language: GCCV2Language) => string;
}

interface UseGCCV2TabResultReturn {
  loading: boolean;
  error: string | null;
  parsed: GCCV2ParsedResponse | null;
  language: GCCV2Language;
  refresh: () => void;
  sendFollowUp: (message: string) => Promise<void>;
  goToChat: () => Promise<void>;
}

export function useGCCV2TabResult({
  tabKey,
  subCategoryMatch,
  buildPrompt,
}: UseGCCV2TabResultOptions): UseGCCV2TabResultReturn {
  const navigate = useNavigate();
  const {
    chats,
    currentChat,
    sendMessageStream,
    startNewChat,
    setCurrentChat,
  } = useChat();
  const { categories } = useCategories();
  const { subCategories } = useSubCategories();

  const language = useGCCV2Language();
  // Keyed by language so a preferred-language change never shows a reading
  // generated in the old language.
  const sessionKey = `astriaGCCV2_${tabKey}_${language}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<GCCV2ParsedResponse | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const chatsRef = useRef(chats);
  const currentChatRef = useRef(currentChat);
  chatsRef.current = chats;
  currentChatRef.current = currentChat;

  const activeChatIdRef = useRef<string | null>(null);
  // categories/subCategories are separate contexts that populate
  // asynchronously, so the effect below can re-run several times before
  // both are ready. Track the refreshTick we've already fetched for so a
  // second "both non-empty" render doesn't fire sendMessageStream again
  // (which would double the Gemini call for a single tab load).
  const fetchedTickRef = useRef<number | null>(null);

  // Component-level mount flag — deliberately NOT the "let isMounted = true;
  // ...; return () => { isMounted = false }" pattern scoped inside the
  // effect below. Under StrictMode (or any double-invoke), React mounts,
  // cleans up, and mounts again: the first invocation is the one that calls
  // fetchResult() (fetchedTickRef then blocks the second invocation from
  // re-fetching, avoiding a duplicate Gemini call), but its own cleanup
  // fires immediately, so an effect-scoped isMounted would be false by the
  // time that fetch resolves — its setLoading(false)/setParsed() would be
  // silently skipped even though the component is genuinely still mounted,
  // leaving the tab stuck on the loading spinner until a manual refresh.
  // This ref is only flipped false on true unmount, so whichever invocation
  // actually owns the in-flight fetch can always apply its result.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const dob = getStoredAstriaGCCV2Dob();
    if (!dob) {
      navigate(`/astria-gcc-v2/dob?next=/astria-gcc-v2/${tabKey}`);
      return;
    }

    if (refreshTick === 0) {
      try {
        const cached = sessionStorage.getItem(sessionKey);
        if (cached) {
          setParsed(parseGCCV2Response(cached));
          setLoading(false);
          return;
        }
      } catch {
        // ignore sessionStorage read failures
      }
    }

    // CategoryContext/SubCategoryContext fetch asynchronously on mount, so on
    // a fresh page load (e.g. right after the DOB form's navigate()) this
    // effect can run before either has populated — categories/subCategories
    // are both still []. Sending the prompt at that point resolves
    // categoryId/subCategoryId to null, which the backend has no dedicated
    // handling for, producing a wrong/generic reply. Wait for the next
    // categories/subCategories update instead of firing with an empty list.
    if (categories.length === 0 || subCategories.length === 0) {
      return;
    }

    if (fetchedTickRef.current === refreshTick) {
      return;
    }
    fetchedTickRef.current = refreshTick;

    const fetchResult = async () => {
      setLoading(true);
      setError(null);
      try {
        const category = categories.find(
          (c) => c.name === GCC_V2_CATEGORY_NAME,
        );
        const subCategory = subCategories.find(
          (sc) =>
            sc.categoryId === category?.id &&
            sc.name.toLowerCase().includes(subCategoryMatch),
        );

        const categoryId = category?.id || null;
        const subCategoryId = subCategory?.id || null;

        const existingChat = chatsRef.current.find(
          (c) =>
            c.categoryId === categoryId && c.subCategoryId === subCategoryId,
        );
        const chatId = existingChat?.id;
        activeChatIdRef.current = chatId || null;

        const dobValue = getStoredAstriaGCCV2Dob() || "";
        const message = buildPrompt(dobValue, language);

        const aiContent = await sendMessageStream(
          message,
          chatId,
          categoryId,
          subCategoryId,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
        );

        // Prefer the value sendMessageStream just resolved with — it's
        // built from a local accumulator inside that call, so it's never
        // stale. Falling back to chats/currentChat state (via refs) covers
        // older callers, but that state can lag a render behind right after
        // the stream ends, which previously left this tab stuck on loading
        // until a manual refresh re-ran the effect against settled state.
        const updatedChat =
          currentChatRef.current?.categoryId === categoryId &&
          currentChatRef.current?.subCategoryId === subCategoryId
            ? currentChatRef.current
            : chatsRef.current.find(
                (c) =>
                  c.categoryId === categoryId &&
                  c.subCategoryId === subCategoryId,
              );

        // For a brand-new chat (no chatId going in), sendMessageStream
        // creates it internally — this is still the only place that learns
        // the real chat id, needed later by sendFollowUp/goToChat.
        activeChatIdRef.current = updatedChat?.id || activeChatIdRef.current;

        let finalContent = aiContent;
        if (!finalContent) {
          const lastAiMsg = updatedChat?.messages
            ?.filter((m: any) => m.sender === "ai")
            .slice(-1)[0];
          finalContent = lastAiMsg?.content || null;
        }

        if (finalContent) {
          try {
            sessionStorage.setItem(sessionKey, finalContent);
          } catch {
            // ignore sessionStorage write failures
          }
          if (isMountedRef.current) {
            setParsed(parseGCCV2Response(finalContent));
            setLoading(false);
          }
        } else {
          if (isMountedRef.current) {
            setError(GENERIC_ERROR[language]);
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("GCC v2 tab result error:", err);
        if (isMountedRef.current) {
          setError("Something went wrong. Please try again.");
          setLoading(false);
        }
      }
    };

    fetchResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, categories, subCategories, language]);

  const refresh = () => {
    try {
      sessionStorage.removeItem(sessionKey);
    } catch {
      // ignore
    }
    setRefreshTick((t) => t + 1);
  };

  const sendFollowUp = async (message: string) => {
    const category = categories.find((c) => c.name === GCC_V2_CATEGORY_NAME);
    const subCategory = subCategories.find(
      (sc) =>
        sc.categoryId === category?.id &&
        sc.name.toLowerCase().includes(subCategoryMatch),
    );
    const categoryId = category?.id || null;
    const subCategoryId = subCategory?.id || null;

    // The backend picks reply language by detecting script in the message
    // text (detectLangFromMessage). A follow-up typed in English/plain text
    // while Arabic is selected would otherwise silently detect as English —
    // append the same strict language cue used by the tab's initial
    // buildPrompt() templates so the selected language always wins, headings
    // included.
    const languageTaggedMessage = `${message}\n${getGCCV2StrictLanguageInstruction(language)}`;

    await sendMessageStream(
      languageTaggedMessage,
      activeChatIdRef.current || undefined,
      categoryId,
      subCategoryId,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    );

    const updatedChat = chatsRef.current.find(
      (c) => c.categoryId === categoryId && c.subCategoryId === subCategoryId,
    );
    const lastAiMsg = updatedChat?.messages
      ?.filter((m: any) => m.sender === "ai")
      .slice(-1)[0];

    if (lastAiMsg?.content) {
      setParsed(parseGCCV2Response(lastAiMsg.content));
    }
  };

  // Redirects to the normal chat UI (/sanctuary), continuing the same
  // categoryId/subCategoryId-scoped conversation this tab already used to
  // fetch its reading — mirrors the startNewChat → setCurrentChat →
  // navigate('/sanctuary?chat=...') pattern used elsewhere in the app
  // (e.g. EnergyMatchDesign.tsx's country-module chat handoff).
  // const goToChat = async () => {
  //   const category = categories.find((c) => c.name === GCC_V2_CATEGORY_NAME);
  //   const subCategory = subCategories.find(
  //     (sc) =>
  //       sc.categoryId === category?.id &&
  //       sc.name.toLowerCase().includes(subCategoryMatch),
  //   );
  //   const categoryId = category?.id || null;
  //   const subCategoryId = subCategory?.id || null;

  //   let chatId = activeChatIdRef.current;
  //   if (!chatId) {
  //     const existingChat = chatsRef.current.find(
  //       (c) => c.categoryId === categoryId && c.subCategoryId === subCategoryId,
  //     );
  //     if (existingChat) {
  //       chatId = existingChat.id;
  //     } else {
  //       const newChat = await startNewChat(
  //         categoryId || undefined,
  //         subCategoryId || undefined,
  //       );
  //       chatId = newChat.id;
  //     }
  //   }

  //   const chatToOpen = chatsRef.current.find((c) => c.id === chatId);
  //   if (chatToOpen) {
  //     setCurrentChat(chatToOpen);
  //   }
  //   navigate(`/sanctuary?chat=${chatId}`);
  // };
  const goToChat = async () => {
    try {
      const category = categories.find((c) => c.name === GCC_V2_CATEGORY_NAME);
      const subCategory = subCategories.find(
        (sc) =>
          sc.categoryId === category?.id &&
          sc.name.toLowerCase().includes(subCategoryMatch),
      );
      const categoryId = category?.id || null;
      const subCategoryId = subCategory?.id || null;

      // Always create a fresh chat
      const newChat = await startNewChat(
        categoryId || undefined,
        subCategoryId || undefined,
      );

      // Update state
      setCurrentChat(newChat);

      // Navigate to the new chat
      navigate(`/sanctuary?chat=${newChat.id}`);

      // Optional: Clear cache so the tab shows fresh data if user returns
      try {
        sessionStorage.removeItem(sessionKey);
      } catch {
        // ignore
      }
      setRefreshTick((t) => t + 1);
    } catch (error) {
      console.error("Failed to create new chat:", error);
      setError(NEW_CHAT_ERROR[language]);
    }
  };

  return { loading, error, parsed, language, refresh, sendFollowUp, goToChat };
}
